/**
 * Crypto Rescue Mode — React Query hooks over tRPC.
 *
 * Powers the CRM v2 activation Card (apps/crm-app/src/components/form/store/
 * crypto-rescue-box.tsx) and the funnel step editor's rescue indicator.
 * The progress modal polls `useCryptoRescueJob` every 1.5s while a bake is
 * pending or running.
 */
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';

import { trpc } from './trpc';

type CryptoRescueOutputs = inferRouterOutputs<AppRouter>['cryptoRescue'];

export type StoreRescueState = CryptoRescueOutputs['getStoreState'];
export type CryptoRescueJob = CryptoRescueOutputs['getJob'];

export function useStoreRescueState(storeId: string, enabled = true) {
  return trpc.cryptoRescue.getStoreState.useQuery(
    { storeId },
    {
      enabled: enabled && !!storeId,
      staleTime: 5_000,
    },
  );
}

export function useCryptoRescueJob(jobId: string | null) {
  return trpc.cryptoRescue.getJob.useQuery(
    { jobId: jobId ?? '' },
    {
      enabled: !!jobId,
      refetchInterval: (query) => {
        const job = query.state.data;
        if (!job) return 1500;
        return job.status === 'pending' || job.status === 'running' ? 1500 : false;
      },
      refetchIntervalInBackground: true,
    },
  );
}

export function useActivateCryptoRescue() {
  const utils = trpc.useUtils();
  return trpc.cryptoRescue.activate.useMutation({
    onSuccess: (_, vars) => {
      utils.cryptoRescue.getStoreState.invalidate({ storeId: vars.storeId });
    },
  });
}

export function useDeactivateCryptoRescue() {
  const utils = trpc.useUtils();
  return trpc.cryptoRescue.deactivate.useMutation({
    onSuccess: (_, vars) => {
      utils.cryptoRescue.getStoreState.invalidate({ storeId: vars.storeId });
    },
  });
}

export function useCancelCryptoRescueJob(storeId: string) {
  const utils = trpc.useUtils();
  return trpc.cryptoRescue.cancelJob.useMutation({
    onSuccess: () => {
      utils.cryptoRescue.getStoreState.invalidate({ storeId });
    },
  });
}
